// src/components/Agenda.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "../supabaseClient";
import "./Agenda.css";
import {
  FaTimes, FaChevronLeft, FaChevronRight, FaSearch, FaPlus,
  FaCheckCircle, FaRegCircle, FaTrash, FaCalendarAlt, FaFileAlt,
  FaFolderOpen, FaStream, FaListUl, FaRegCalendarAlt, FaArrowRight
} from "react-icons/fa";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useNavigate } from "react-router-dom";

const MESES_CURTO = ["JAN","FEV","MAR","ABR","MAI","JUN","JUL","AGO","SET","OUT","NOV","DEZ"];
const MESES_LONGO = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
const DIAS_SEMANA = ["DOM","SEG","TER","QUA","QUI","SEX","SÁB"];

const Agenda = ({ user, currentContainerId, onClose, onOpenItem, onNovaTarefa }) => {
  const navigate = useNavigate();
  const [objetivosCompletos, setObjetivosCompletos] = useState(null);
  const [comentariosAgendados, setComentariosAgendados] = useState(null);
  const [notasRapidasCompletas, setNotasRapidasCompletas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItemType, setEditingItemType] = useState(null);
  const [editingDate, setEditingDate] = useState('');
  const [hideSemData, setHideSemData] = useState(true);
  const [showSidebarAgenda, setShowSidebarAgenda] = useState(false);

  const [viewMode, setViewMode] = useState('timeline');
  const [searchText, setSearchText] = useState('');
  const [filterProjeto, setFilterProjeto] = useState('Todos');
  const [filterOrigem, setFilterOrigem] = useState('Todas');
  const [filterResponsavel, setFilterResponsavel] = useState('Todos');
  const [quickFilter, setQuickFilter] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [visibleDaysCount, setVisibleDaysCount] = useState(3);

  const hoje = new Date();
  const [calMonth, setCalMonth] = useState(hoje.getMonth() + 1);
  const [calYear, setCalYear] = useState(hoje.getFullYear());

  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === "Escape") onClose(); };
    const handleClickOutside = (e) => {
      if (e.target.classList.contains("agenda-modal-overlay")) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const normalizarTexto = (str) => {
    if (!str) return "";
    return str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^\w]/g, "").trim();
  };

  const fetchData = useCallback(async () => {
    if (!user?.id || !currentContainerId) {
      setError("Usuário ou container não definidos.");
      setLoading(false);
      return;
    }

    try {
      const { data: pref, error: prefErr } = await supabase
        .from("user_preferences")
        .select("value")
        .eq("user_id", user.id)
        .eq("container_id", currentContainerId)
        .eq("key", "show_sidebar_agenda")
        .single();
      if (!prefErr) setShowSidebarAgenda(pref?.value || false);
      else if (prefErr.code !== "PGRST116") console.warn("Erro ao carregar preferência:", prefErr);
    } catch (err) {
      console.warn("Exceção ao carregar preferência", err);
    }

    try {
      // ========== Objetivos ==========
      const { data: responsaveis, error: err1 } = await supabase
        .from("ata_objetivos_responsaveis_enriquecidos")
        .select("ata_objetivo_id")
        .eq("usuario_id", user.id);
      if (err1) throw err1;

      let objetivosCompletos = [];
      if (responsaveis && Array.isArray(responsaveis)) {
        const objetivoIds = responsaveis.map(r => r.ata_objetivo_id).filter(id => id != null);

        if (objetivoIds.length > 0) {
          const { data: objetivos, error: err2 } = await supabase
            .from("ata_objetivos")
            .select("id, texto, data_entrega, ata_id, concluido, task_nota_id")
            .in("id", objetivoIds)
            .order("data_entrega", { ascending: true });
          if (err2) throw err2;

          if (objetivos && Array.isArray(objetivos)) {
            const mapaUnicos = new Map();
            objetivos.forEach(obj => {
              if (obj.texto?.startsWith("[EXCLUIDO]")) return;
              const norm = normalizarTexto(obj.texto);
              if (!mapaUnicos.has(norm) || obj.id > mapaUnicos.get(norm).id) mapaUnicos.set(norm, obj);
            });
            const objetivosUnicos = Array.from(mapaUnicos.values());

            const ataIds = [...new Set(objetivosUnicos.map(o => o.ata_id).filter(id => id != null))];
            const taskNotaIds = [...new Set(objetivosUnicos.map(o => o.task_nota_id).filter(id => id != null))];
            let atasMap = {};
            const projetoIds = new Set();
            const setorIds = new Set();
            const pilhaIds = new Set();
            const notaIds = new Set();

            if (ataIds.length > 0) {
              const { data: atas, error: err3 } = await supabase
                .from("atas")
                .select("id, nota_id, pilha_id, projeto_id, setor_id")
                .in("id", ataIds);
              if (err3) throw err3;
              if (Array.isArray(atas)) {
                atas.forEach(ata => {
                  atasMap[ata.id] = { nota_id: ata.nota_id, pilha_id: ata.pilha_id, projeto_id: ata.projeto_id, setor_id: ata.setor_id };
                  if (ata.nota_id) notaIds.add(ata.nota_id);
                  if (ata.pilha_id) pilhaIds.add(ata.pilha_id);
                  if (ata.projeto_id) projetoIds.add(ata.projeto_id);
                  if (ata.setor_id) setorIds.add(ata.setor_id);
                });
              }
            }

            let notasTarefaMap = {};
            if (taskNotaIds.length > 0) {
              const { data: notasTarefa } = await supabase
                .from("notas")
                .select("id, pilha_id")
                .in("id", taskNotaIds);
              if (Array.isArray(notasTarefa)) {
                notasTarefa.forEach(n => {
                  notasTarefaMap[n.id] = { pilha_id: n.pilha_id };
                  if (n.pilha_id) pilhaIds.add(n.pilha_id);
                });
              }
            }

            const notaIdsParaCompromissos = [...new Set([...taskNotaIds, ...notaIds])];
            let compromissosMap = {};
            if (notaIdsParaCompromissos.length > 0) {
              const { data: comps } = await supabase
                .from("compromissos")
                .select("id, nota_id, titulo, data_compromisso, hora_inicio, concluido")
                .in("nota_id", notaIdsParaCompromissos);
              if (Array.isArray(comps)) {
                comps.forEach(c => {
                  if (!compromissosMap[c.nota_id]) compromissosMap[c.nota_id] = [];
                  compromissosMap[c.nota_id].push(c);
                });
              }
            }

            let notasMap = {};
            if (notaIds.size > 0) {
              const { data: notas, error: errNotas } = await supabase.from("notas").select("id, nome, pilha_id").in("id", Array.from(notaIds));
              if (!errNotas && Array.isArray(notas)) {
                notas.forEach(n => {
                  notasMap[n.id] = { nome: n.nome || "Nota sem título", pilha_id: n.pilha_id };
                  if (n.pilha_id) pilhaIds.add(n.pilha_id);
                });
              }
            }

            let pilhasMap = {};
            if (pilhaIds.size > 0) {
              const { data: pilhas, error: err4 } = await supabase
                .from("pilhas")
                .select("id, title, project_id, setor_id")
                .in("id", Array.from(pilhaIds));
              if (!err4 && Array.isArray(pilhas)) {
                pilhasMap = Object.fromEntries(
                  pilhas.map(p => [p.id, { title: p.title || "Pilha sem nome", project_id: p.project_id, setor_id: p.setor_id }])
                );
                pilhas.forEach(p => {
                  if (p.project_id) projetoIds.add(p.project_id);
                  if (p.setor_id) setorIds.add(p.setor_id);
                });
              }
            }

            const userIdsDonos = new Set();

            let projetosMap = {};
            if (projetoIds.size > 0) {
              const { data: projects, error: err5 } = await supabase.from("projects").select("id, name, user_id").in("id", Array.from(projetoIds));
              if (!err5 && Array.isArray(projects)) {
                projects.forEach(proj => {
                  projetosMap[proj.id] = { nome: proj.name || "Projeto sem nome", user_id: proj.user_id };
                  if (proj.user_id) userIdsDonos.add(proj.user_id);
                });
              }
            }

            let setoresMap = {};
            if (setorIds.size > 0) {
              const { data: setores, error: err5b } = await supabase.from("setores").select("id, name, user_id").in("id", Array.from(setorIds));
              if (!err5b && Array.isArray(setores)) {
                setores.forEach(s => {
                  setoresMap[s.id] = { nome: s.name || "Setor sem nome", user_id: s.user_id };
                  if (s.user_id) userIdsDonos.add(s.user_id);
                });
              }
            }

            let donosMap = {};
            if (userIdsDonos.size > 0) {
              const { data: profiles, error: err6 } = await supabase.from("profiles").select("id, nome, container").in("id", Array.from(userIdsDonos));
              if (!err6 && Array.isArray(profiles)) {
                donosMap = Object.fromEntries(
                  profiles.map(p => [p.id, { nomeDono: p.nome || "Usuário", nomeContainer: p.container || p.nome || "Container sem nome" }])
                );
              }
            }

            objetivosCompletos = objetivosUnicos.map(obj => {
              const ata = atasMap[obj.ata_id] || {};
              const notaTarefa = obj.task_nota_id ? notasTarefaMap[obj.task_nota_id] : null;
              const notaAta = ata.nota_id ? notasMap[ata.nota_id] : null;
              const pilhaIdEfetivo = notaTarefa?.pilha_id || notaAta?.pilha_id || ata.pilha_id || null;
              const pilhaInfo = pilhaIdEfetivo ? pilhasMap[pilhaIdEfetivo] : null;

              const projetoId = pilhaInfo?.project_id || ata.projeto_id || null;
              const setorId = pilhaInfo?.setor_id || ata.setor_id || null;
              const projeto = projetoId ? projetosMap[projetoId] : null;
              const setor = !projeto && setorId ? setoresMap[setorId] : null;
              const donoUserId = projeto?.user_id || setor?.user_id || null;
              const dono = donoUserId ? donosMap[donoUserId] : null;

              const notaAlvoId = obj.task_nota_id || ata.nota_id || null;
              const nomeNota = notaAta?.nome || (ata.nota_id ? "Nota não encontrada" : "Nota não associada");

              return {
                ...obj,
                nomeNota,
                nomePilha: pilhaInfo ? pilhaInfo.title : "–",
                nomeProjeto: projeto?.nome || setor?.nome || "–",
                nomeDono: dono ? dono.nomeDono : "Sem dono",
                nomeContainer: dono ? dono.nomeContainer : "Sem container",
                _chaveNota: obj.ata_id,
                _notaId: notaAlvoId,
                _pilhaId: pilhaIdEfetivo,
                _projetoId: projetoId,
                _setorId: setorId,
                _containerId: donoUserId,
                _compromissos: notaAlvoId ? (compromissosMap[notaAlvoId] || []) : [],
              };
            });
          }
        }
      }

      // ========== Notas Rápidas ==========
      const { data: todasNotasRapidas } = await supabase
        .from("notas")
        .select("id, nome, descricao, data_entrega, pilha_id, responsavel_id, responsaveis_ids, created_at, concluida")
        .eq("tipo", "Nota Rápida")
        .not("data_entrega", "is", null)
        .order("data_entrega", { ascending: true });

      const notasRapidas = todasNotasRapidas?.filter(nota => {
        const eResponsavel = nota.responsavel_id === user.id;
        const estaNoArray = nota.responsaveis_ids && Array.isArray(nota.responsaveis_ids) && nota.responsaveis_ids.includes(user.id);
        return eResponsavel || estaNoArray;
      }) || [];

      let notasRapidasCompletas = null;
      if (notasRapidas.length > 0) {
        const pilhaIdsNotas = [...new Set(notasRapidas.map(n => n.pilha_id).filter(id => id))];
        let pilhasMap = {};
        const projetoIdsDasPilhas = new Set();
        if (pilhaIdsNotas.length > 0) {
          const { data: pilhas } = await supabase.from("pilhas").select("id, title, project_id").in("id", pilhaIdsNotas);
          if (Array.isArray(pilhas)) pilhas.forEach(p => {
            pilhasMap[p.id] = { title: p.title || "Pilha sem nome", project_id: p.project_id };
            if (p.project_id) projetoIdsDasPilhas.add(p.project_id);
          });
        }
        let projetosMap = {};
        const userIdsDonos = new Set();
        if (projetoIdsDasPilhas.size > 0) {
          const { data: projetos } = await supabase.from("projects").select("id, name, user_id").in("id", Array.from(projetoIdsDasPilhas));
          if (Array.isArray(projetos)) projetos.forEach(proj => {
            projetosMap[proj.id] = { nome: proj.name || "Projeto sem nome", user_id: proj.user_id };
            if (proj.user_id) userIdsDonos.add(proj.user_id);
          });
        }
        let donosMap = {};
        if (userIdsDonos.size > 0) {
          const { data: profiles } = await supabase.from("profiles").select("id, nome, container").in("id", Array.from(userIdsDonos));
          if (Array.isArray(profiles)) donosMap = Object.fromEntries(profiles.map(p => [p.id, { nomeDono: p.nome || "Usuário", nomeContainer: p.container || p.nome || "Container sem nome" }]));
        }
        notasRapidasCompletas = notasRapidas.map(nota => {
          const pilha = nota.pilha_id ? pilhasMap[nota.pilha_id] : null;
          const projeto = pilha?.project_id ? projetosMap[pilha.project_id] : null;
          const dono = projeto?.user_id ? donosMap[projeto.user_id] : null;
          return {
            ...nota,
            nomeNota: nota.nome || "Nota rápida sem título",
            nomePilha: pilha ? pilha.title : "Sem pilha",
            nomeProjeto: projeto ? projeto.nome : "Sem projeto",
            nomeDono: dono ? dono.nomeDono : "Sem dono",
            nomeContainer: dono ? dono.nomeContainer : "Sem container",
            _chaveNota: nota.id,
            _notaId: nota.id,
            _pilhaId: nota.pilha_id || null,
            _projetoId: pilha?.project_id || null,
            _setorId: null,
            _containerId: projeto?.user_id || null,
          };
        });
      }

      // ========== Comentários ==========
      const { data: comentarios } = await supabase
        .from("comentarios")
        .select("id, conteudo, created_at, nota_id, user_id, data_entrega")
        .eq("agendado_por", user.id)
        .order("created_at", { ascending: false });

      let comentariosCompletos = [];
      if (comentarios && comentarios.length > 0) {
        const userIdsAutores = [...new Set(comentarios.map(c => c.user_id).filter(id => id))];
        const notaIds = [...new Set(comentarios.map(c => c.nota_id).filter(id => id != null))];

        let autoresMap = {};
        if (userIdsAutores.length > 0) {
          const { data: autores } = await supabase.from("profiles").select("id, nome, nickname").in("id", userIdsAutores);
          if (Array.isArray(autores)) autoresMap = Object.fromEntries(autores.map(a => [a.id, a.nickname || a.nome || "Usuário"]));
        }

        let notasMap = {};
        const pilhaIdsNotas = new Set();
        if (notaIds.length > 0) {
          const { data: notas } = await supabase.from("notas").select("id, nome, pilha_id").in("id", notaIds);
          if (Array.isArray(notas)) notas.forEach(nota => {
            notasMap[nota.id] = { nome: nota.nome || "Sem título", pilha_id: nota.pilha_id };
            if (nota.pilha_id) pilhaIdsNotas.add(nota.pilha_id);
          });
        }

        let pilhasNotasMap = {};
        const projetoIdsDasPilhas = new Set();
        if (pilhaIdsNotas.size > 0) {
          const { data: pilhas } = await supabase.from("pilhas").select("id, title, project_id").in("id", Array.from(pilhaIdsNotas));
          if (Array.isArray(pilhas)) pilhas.forEach(p => {
            pilhasNotasMap[p.id] = { title: p.title || "Pilha sem nome", project_id: p.project_id };
            if (p.project_id) projetoIdsDasPilhas.add(p.project_id);
          });
        }

        let projetosNotasMap = {};
        if (projetoIdsDasPilhas.size > 0) {
          const { data: projetos } = await supabase.from("projects").select("id, name, user_id").in("id", Array.from(projetoIdsDasPilhas));
          if (Array.isArray(projetos)) {
            projetos.forEach(p => {
              projetosNotasMap[p.id] = { nome: p.name || "Projeto sem nome", user_id: p.user_id };
            });
          }
        }

        comentariosCompletos = comentarios.map(com => {
          const nota = notasMap[com.nota_id] || {};
          const pilha = nota.pilha_id ? pilhasNotasMap[nota.pilha_id] : null;
          const projeto = pilha?.project_id ? projetosNotasMap[pilha.project_id] : null;
          return {
            ...com,
            nomeAutor: autoresMap[com.user_id] || "Usuário",
            nomeNota: nota.nome || (com.nota_id ? "Nota não encontrada" : "Sem nota"),
            nomeProjeto: projeto ? projeto.nome : "Sem projeto",
            nomePilha: pilha ? pilha.title : "Sem pilha",
            nomeDono: "Não informado",
            nomeContainer: "Não informado",
            _chaveNota: com.nota_id,
            _notaId: com.nota_id || null,
            _pilhaId: nota.pilha_id || null,
            _projetoId: pilha?.project_id || null,
            _setorId: null,
            _containerId: projeto?.user_id || null,
          };
        });
      }

      setObjetivosCompletos(objetivosCompletos);
      setComentariosAgendados(comentariosCompletos);
      setNotasRapidasCompletas(notasRapidasCompletas);
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Erro ao carregar agenda:", err);
      setError({ message: err.message || "Erro desconhecido", details: err.details, code: err.code });
      setObjetivosCompletos([]);
      setComentariosAgendados([]);
      setNotasRapidasCompletas([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, currentContainerId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ---------- Helpers de data ----------
  const calcularDiasRestantes = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    const h = new Date(); h.setHours(0, 0, 0, 0);
    const dataAlvo = new Date(year, month - 1, day); dataAlvo.setHours(0, 0, 0, 0);
    return Math.ceil((dataAlvo - h) / 86400000);
  };

  const getDiaSemana = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return DIAS_SEMANA[new Date(year, month - 1, day).getDay()];
  };

  const isConcluido = (item) => (item.tipo === 'objetivo' && item.concluido) || (item.tipo === 'nota_rapida' && item.concluida);

  const origemLabel = (tipo) => tipo === 'objetivo' ? 'Tarefa' : tipo === 'comentario' ? 'Compromisso' : 'Nota Rápida';

  const rotuloDia = (dateStr, dias) => {
    if (dias === 0) return { texto: 'Hoje', classe: 'hoje' };
    if (dias === 1) return { texto: 'Amanhã', classe: 'amanha' };
    if (dias < 0) return { texto: 'Atrasada', classe: 'atrasada' };
    return { texto: null, classe: 'futuro' };
  };

  // ---------- Ações de escrita ----------
  const toggleConcluido = async (item) => {
    if (item.concluido || item.concluida) return;
    let error = null;
    if (item.tipo === 'objetivo') {
      ({ error } = await supabase.from("ata_objetivos").update({ concluido: true }).eq("id", item.id));
      if (!error) setObjetivosCompletos(prev => prev.map(o => o.id === item.id ? { ...o, concluido: true } : o));
    } else if (item.tipo === 'nota_rapida') {
      ({ error } = await supabase.from("notas").update({ concluida: true }).eq("id", item.id));
      if (!error) setNotasRapidasCompletas(prev => prev.map(n => n.id === item.id ? { ...n, concluida: true } : n));
    }
    if (error) console.error("Erro ao atualizar conclusão:", error);
  };

  const handleDateClick = (item, e) => {
    e?.stopPropagation();
    setEditingItemId(item.id); setEditingItemType(item.tipo); setEditingDate(item.data_entrega || '');
  };

  const handleSave = async () => {
    if (!editingItemId || !editingItemType) return;
    const dateToSave = editingDate || null;
    let error = null;
    const tabela = editingItemType === 'objetivo' ? 'ata_objetivos' : editingItemType === 'comentario' ? 'comentarios' : 'notas';
    ({ error } = await supabase.from(tabela).update({ data_entrega: dateToSave }).eq("id", editingItemId));
    if (!error) {
      if (editingItemType === 'objetivo') setObjetivosCompletos(prev => prev.map(o => o.id === editingItemId ? { ...o, data_entrega: dateToSave } : o));
      else if (editingItemType === 'comentario') setComentariosAgendados(prev => prev.map(c => c.id === editingItemId ? { ...c, data_entrega: dateToSave } : c));
      else setNotasRapidasCompletas(prev => prev.map(n => n.id === editingItemId ? { ...n, data_entrega: dateToSave } : n));
    } else console.error("Erro ao salvar data:", error);
    setEditingItemId(null); setEditingItemType(null); setEditingDate('');
  };

  const handleCancel = (e) => { e?.stopPropagation(); setEditingItemId(null); setEditingItemType(null); setEditingDate(''); };

  const handleExcluirDaAgenda = async (item, e) => {
    e?.stopPropagation();
    const descricao = item.tipo === 'objetivo' ? item.texto : item.tipo === 'comentario' ? item.conteudo : (item.descricao || item.nome || "esta nota");
    if (!window.confirm(`Deseja remover "${descricao}" da agenda?`)) return;
    let error = null;
    if (item.tipo === 'objetivo') {
      ({ error } = await supabase.from("ata_objetivos").update({ texto: `[EXCLUIDO] ${item.texto}` }).eq("id", item.id));
      if (!error) setObjetivosCompletos(prev => prev.filter(o => o.id !== item.id));
    } else if (item.tipo === 'comentario') {
      ({ error } = await supabase.from("comentarios").update({ agendado_por: null }).eq("id", item.id));
      if (!error) setComentariosAgendados(prev => prev.filter(c => c.id !== item.id));
    } else {
      ({ error } = await supabase.from("notas").update({ data_entrega: null }).eq("id", item.id));
      if (!error) setNotasRapidasCompletas(prev => prev.filter(n => n.id !== item.id));
    }
    if (error) { console.error("Erro ao excluir da agenda:", error); alert("Erro ao remover item da agenda."); }
  };

  const toggleSidebarAgenda = async (checked) => {
    if (!user?.id || !currentContainerId) return;
    const { error } = await supabase.from("user_preferences").upsert(
      { user_id: user.id, container_id: currentContainerId, key: "show_sidebar_agenda", value: checked },
      { onConflict: "user_id,container_id,key" }
    );
    if (error) { console.error("Erro ao salvar preferência:", error); return; }
    setShowSidebarAgenda(checked);
    window.dispatchEvent(new CustomEvent('agendaPreferenceUpdated'));
  };

  // ---------- Agrupamento ----------
  const { itensComData, itensSemData } = useMemo(() => {
    const comData = [], semData = [];
    objetivosCompletos?.forEach(obj => {
      const base = { ...obj, tipo: 'objetivo' };
      if (obj.data_entrega) comData.push({ ...base, data_ref: obj.data_entrega, dias: calcularDiasRestantes(obj.data_entrega) });
      else semData.push(base);
    });
    comentariosAgendados?.forEach(com => {
      const base = { ...com, tipo: 'comentario' };
      if (com.data_entrega) comData.push({ ...base, data_ref: com.data_entrega, dias: calcularDiasRestantes(com.data_entrega) });
      else semData.push(base);
    });
    notasRapidasCompletas?.forEach(nota => {
      const base = { ...nota, tipo: 'nota_rapida' };
      if (nota.data_entrega) comData.push({ ...base, data_ref: nota.data_entrega, dias: calcularDiasRestantes(nota.data_entrega) });
      else semData.push(base);
    });
    return { itensComData: comData, itensSemData: semData };
  }, [objetivosCompletos, comentariosAgendados, notasRapidasCompletas]);

  const projetosUnicos = useMemo(() => {
    const set = new Set([...itensComData, ...itensSemData].map(i => i.nomeProjeto).filter(v => v && v !== '–'));
    return ['Todos', ...Array.from(set).sort()];
  }, [itensComData, itensSemData]);

  const responsaveisUnicos = useMemo(() => {
    const set = new Set([...itensComData, ...itensSemData].map(i => i.tipo === 'comentario' ? i.nomeAutor : i.nomeDono).filter(Boolean));
    return ['Todos', ...Array.from(set).sort()];
  }, [itensComData, itensSemData]);

  const passaFiltros = useCallback((item) => {
    if (searchText.trim()) {
      const alvo = normalizarTexto([
        item.texto, item.conteudo, item.descricao, item.nome, item.nomeNota, item.nomeProjeto
      ].filter(Boolean).join(' '));
      if (!alvo.includes(normalizarTexto(searchText))) return false;
    }
    if (filterProjeto !== 'Todos' && item.nomeProjeto !== filterProjeto) return false;
    if (filterOrigem !== 'Todas' && origemLabel(item.tipo) !== filterOrigem) return false;
    if (filterResponsavel !== 'Todos') {
      const resp = item.tipo === 'comentario' ? item.nomeAutor : item.nomeDono;
      if (resp !== filterResponsavel) return false;
    }
    return true;
  }, [searchText, filterProjeto, filterOrigem, filterResponsavel]);

  const itensComDataFiltrados = useMemo(() => itensComData.filter(passaFiltros), [itensComData, passaFiltros]);
  const itensSemDataFiltrados = useMemo(() => itensSemData.filter(passaFiltros), [itensSemData, passaFiltros]);

  const stats = useMemo(() => ({
    hoje: itensComDataFiltrados.filter(i => i.dias === 0).length,
    atrasadas: itensComDataFiltrados.filter(i => i.dias < 0 && !isConcluido(i)).length,
    proximos7: itensComDataFiltrados.filter(i => i.dias > 0 && i.dias <= 7).length,
    semData: itensSemDataFiltrados.length,
    concluidas: itensComDataFiltrados.filter(isConcluido).length + itensSemDataFiltrados.filter(isConcluido).length,
  }), [itensComDataFiltrados, itensSemDataFiltrados]);

  const itensParaTimeline = useMemo(() => {
    return itensComDataFiltrados.filter(i => {
      if (selectedDay) return i.data_ref === selectedDay;
      if (quickFilter === 'hoje') return i.dias === 0;
      if (quickFilter === 'atrasadas') return i.dias < 0 && !isConcluido(i);
      if (quickFilter === 'proximos7') return i.dias >= 0 && i.dias <= 7;
      if (quickFilter === 'concluidas') return isConcluido(i);
      return i.dias >= 0;
    }).sort((a, b) => a.data_ref.localeCompare(b.data_ref));
  }, [itensComDataFiltrados, selectedDay, quickFilter]);

  const gruposPorDia = useMemo(() => {
    const mapa = {};
    itensParaTimeline.forEach(item => {
      if (!mapa[item.data_ref]) mapa[item.data_ref] = [];
      mapa[item.data_ref].push(item);
    });
    return Object.keys(mapa).sort().map(data => ({ data, itens: mapa[data] }));
  }, [itensParaTimeline]);

  const gruposVisiveis = gruposPorDia.slice(0, visibleDaysCount);
  const temMaisDias = gruposPorDia.length > visibleDaysCount;

  useEffect(() => { setVisibleDaysCount(3); }, [quickFilter, selectedDay, filterProjeto, filterOrigem, filterResponsavel, searchText]);

  // ---------- Mini calendário ----------
  const diasComItem = useMemo(() => new Set(itensComDataFiltrados.map(i => i.data_ref)), [itensComDataFiltrados]);

  const gerarGradeCalendario = () => {
    const primeiroDia = new Date(calYear, calMonth - 1, 1).getDay();
    const totalDias = new Date(calYear, calMonth, 0).getDate();
    const celulas = [];
    for (let i = 0; i < primeiroDia; i++) celulas.push(null);
    for (let d = 1; d <= totalDias; d++) celulas.push(d);
    return celulas;
  };

  const mudarMes = (delta) => {
    let m = calMonth + delta, y = calYear;
    if (m > 12) { m = 1; y++; } else if (m < 1) { m = 12; y--; }
    setCalMonth(m); setCalYear(y);
  };

  const formatarDataKey = (d) => `${calYear}-${String(calMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const isHoje = (d) => calYear === hoje.getFullYear() && calMonth === hoje.getMonth() + 1 && d === hoje.getDate();

  const clicarDiaCalendario = (d) => {
    if (!d) return;
    const key = formatarDataKey(d);
    setSelectedDay(prev => prev === key ? null : key);
    setQuickFilter(null);
    setViewMode('timeline');
  };

  const limparFiltros = () => {
    setSearchText(''); setFilterProjeto('Todos'); setFilterOrigem('Todas');
    setFilterResponsavel('Todos'); setQuickFilter(null); setSelectedDay(null);
  };

  const clicarQuickFilter = (tipo) => {
    setSelectedDay(null);
    setQuickFilter(prev => prev === tipo ? null : tipo);
  };

  // ---------- Navegação ----------
  const abrirItem = (item, e) => {
    e?.stopPropagation();
    const entityId = item._projetoId || item._setorId;

    if (entityId && item._containerId) {
      const type = item._projetoId ? "project" : "setor";
      const params = new URLSearchParams({ entityId, type, containerId: item._containerId });
      if (item._notaId) params.set("nota", item._notaId);

      onClose();
      navigate(`/cards/${entityId}?${params.toString()}`, {
        state: {
          projectId: item._projetoId || undefined,
          setorId: item._setorId || undefined,
          pilhaId: item._pilhaId || undefined,
          notaId: item._notaId || undefined,
          entityType: type,
          containerId: item._containerId,
          openNota: !!item._notaId,
        },
      });
      return;
    }

    if (onOpenItem) {
      onOpenItem(item);
      onClose();
    }
  };

  const renderStatusIcon = (item) => {
    if (isConcluido(item)) return <FaCheckCircle className="status-dot concluida" />;
    if (item.tipo === 'comentario') return <FaRegCircle className="status-dot compromisso" />;
    if (item.dias != null && item.dias < 0) return <FaRegCircle className="status-dot atrasada" />;
    return <FaRegCircle className="status-dot tarefa" />;
  };

  const renderTitulo = (item) => item.tipo === 'objetivo' ? item.texto : item.tipo === 'comentario' ? `"${item.conteudo}"` : (item.descricao || item.nome || "Nota rápida");

  const renderLinhaTarefa = (item) => {
    const temDestino = !!((item._projetoId || item._setorId) && item._containerId);
    const clicavel = temDestino || (!!onOpenItem && !!(item._notaId || item._pilhaId || item._projetoId || item._setorId));
    const temDono = item.nomeDono && item.nomeDono !== 'Sem dono' && item.nomeDono !== 'Não informado';
    const temPrazo = item.dias != null;
    const prazoLabel = !temPrazo ? null
      : item.dias < 0 ? `Atrasado ${Math.abs(item.dias)}d`
      : item.dias === 0 ? "Vence hoje"
      : item.dias === 1 ? "Vence amanhã"
      : `Faltam ${item.dias}d`;
    const prazoClasse = !temPrazo ? '' : item.dias < 0 ? 'row-prazo--atrasado' : item.dias <= 1 ? 'row-prazo--urgente' : 'row-prazo--ok';

    return (
      <div
        key={`${item.tipo}-${item.id}`}
        className={`agenda-row ${isConcluido(item) ? 'completed' : ''} ${clicavel ? 'clickable' : ''}`}
        onClick={(e) => clicavel && abrirItem(item, e)}
        title={clicavel ? "Clique para abrir" : "Sem projeto/pilha vinculados"}
      >
        <div className="row-status" onClick={(e) => { e.stopPropagation(); toggleConcluido(item); }}>
          {renderStatusIcon(item)}
        </div>
        <div className="row-body">
          <div className="row-title">{renderTitulo(item)}</div>
          <div className="row-meta">
            <span className="row-tag"><FaFileAlt /> {origemLabel(item.tipo)}</span>
            {item.tipo === 'comentario' && <span className="row-tag">Por {item.nomeAutor}</span>}
            {!isConcluido(item) && prazoLabel && (
              <span className={`row-tag row-prazo ${prazoClasse}`}>{prazoLabel}</span>
            )}
            {item._compromissos?.length > 0 && (
              <span className="row-tag row-tag--compromisso" title={item._compromissos.map(c => c.titulo).join(', ')}>
                <FaCalendarAlt /> {item._compromissos.length} compromisso{item._compromissos.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        <div className="row-project">
          <span className="row-project-name"><FaFolderOpen /> {item.nomeProjeto}</span>
          {item.nomePilha && item.nomePilha !== '–' && <span className="row-pilha">{item.nomePilha}</span>}
          {temDono && <span className="row-owner">{item.nomeDono} • {item.nomeContainer}</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          {clicavel && (
            <span
              title="Ir até a nota"
              onClick={(e) => abrirItem(item, e)}
              style={{ display: "flex", alignItems: "center", padding: "0 6px", color: "#3b82f6", cursor: "pointer" }}
            >
              <FaArrowRight />
            </span>
          )}
          <button className="row-delete" title="Remover da agenda" onClick={(e) => handleExcluirDaAgenda(item, e)}>
            <FaTrash />
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="agenda-modal-overlay">
        <div className="agenda-modal">
          <div className="agenda-loading-animation">
            <DotLottieReact src="https://lottie.host/9780c0a4-579c-4cdb-a974-4205aa670757/qDN0lkVs2V.lottie" loop autoplay style={{ width: '120px', height: '120px' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="agenda-modal-overlay">
      <div className="agenda-modal">
        <div className="agenda-header">
          <div className="agenda-header-left">
            <div className="agenda-header-icon"><FaRegCalendarAlt /></div>
            <div>
              <h2>Minha Agenda</h2>
              <p className="agenda-subtitle">Acompanhe seus compromissos e tarefas</p>
            </div>
          </div>
          <button className="agenda-close-btn" onClick={onClose} aria-label="Fechar"><FaTimes /></button>
        </div>

        {error && <div className="agenda-error"><strong>Erro:</strong> {error.message}</div>}

        {!error && (
          <div className="agenda-body">
            <aside className="agenda-sidebar">
              <div className="mini-cal">
                <div className="mini-cal-header">
                  <button onClick={() => mudarMes(-1)}><FaChevronLeft /></button>
                  <span>{MESES_LONGO[calMonth - 1]} {calYear}</span>
                  <button onClick={() => mudarMes(1)}><FaChevronRight /></button>
                </div>
                <div className="mini-cal-weekdays">
                  {DIAS_SEMANA.map(d => <span key={d}>{d}</span>)}
                </div>
                <div className="mini-cal-grid">
                  {gerarGradeCalendario().map((d, idx) => {
                    if (!d) return <span key={idx} className="cal-cell empty" />;
                    const key = formatarDataKey(d);
                    const temItem = diasComItem.has(key);
                    return (
                      <button
                        key={idx}
                        className={`cal-cell ${isHoje(d) ? 'today' : ''} ${selectedDay === key ? 'selected' : ''}`}
                        onClick={() => clicarDiaCalendario(d)}
                      >
                        {d}
                        {temItem && <span className="cal-dot" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="sidebar-section">
                <h4>Visão rápida</h4>
                <button className={`quick-item ${quickFilter === 'hoje' ? 'active' : ''}`} onClick={() => clicarQuickFilter('hoje')}>
                  <span>Hoje</span><b>{stats.hoje}</b>
                </button>
                <button className={`quick-item ${quickFilter === 'atrasadas' ? 'active' : ''}`} onClick={() => clicarQuickFilter('atrasadas')}>
                  <span>Atrasadas</span><b className="danger">{stats.atrasadas}</b>
                </button>
                <button className={`quick-item ${quickFilter === 'proximos7' ? 'active' : ''}`} onClick={() => clicarQuickFilter('proximos7')}>
                  <span>Próximos 7 dias</span><b>{stats.proximos7}</b>
                </button>
                <button className="quick-item" onClick={() => setHideSemData(!hideSemData)}>
                  <span>Sem data</span><b>{stats.semData}</b>
                </button>
                <button className={`quick-item ${quickFilter === 'concluidas' ? 'active' : ''}`} onClick={() => clicarQuickFilter('concluidas')}>
                  <span>Concluídas</span><b className="success">{stats.concluidas}</b>
                </button>
              </div>

              <div className="sidebar-section">
                <h4>Filtros</h4>
                <label className="filter-label">Projeto
                  <select value={filterProjeto} onChange={e => setFilterProjeto(e.target.value)}>
                    {projetosUnicos.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </label>
                <label className="filter-label">Origem
                  <select value={filterOrigem} onChange={e => setFilterOrigem(e.target.value)}>
                    {['Todas', 'Tarefa', 'Compromisso', 'Nota Rápida'].map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
                <label className="filter-label">Responsável
                  <select value={filterResponsavel} onChange={e => setFilterResponsavel(e.target.value)}>
                    {responsaveisUnicos.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
                <button className="clear-filters-btn" onClick={limparFiltros}>Limpar filtros</button>
              </div>

              <label className="agenda-sidebar-toggle">
                <input type="checkbox" checked={showSidebarAgenda} onChange={(e) => toggleSidebarAgenda(e.target.checked)} />
                Exibir agenda no container
              </label>
            </aside>

            <main className="agenda-main">
              <div className="agenda-toolbar">
                <div className="agenda-search">
                  <FaSearch />
                  <input
                    type="text"
                    placeholder="Buscar tarefas, compromissos..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
                <div className="view-toggle">
                  <button className={viewMode === 'timeline' ? 'active' : ''} onClick={() => setViewMode('timeline')}><FaStream /> Linha do tempo</button>
                  <button className={viewMode === 'lista' ? 'active' : ''} onClick={() => setViewMode('lista')}><FaListUl /> Lista</button>
                  <button className={viewMode === 'calendario' ? 'active' : ''} onClick={() => setViewMode('calendario')}><FaCalendarAlt /> Calendário</button>
                </div>
                {onNovaTarefa && (
                  <button className="nova-tarefa-btn" onClick={onNovaTarefa}><FaPlus /> Nova tarefa</button>
                )}
              </div>

              {selectedDay && (
                <div className="active-filter-chip">
                  Filtrando por {selectedDay.split('-').reverse().join('/')}
                  <button onClick={() => setSelectedDay(null)}><FaTimes /></button>
                </div>
              )}

              {viewMode === 'timeline' && (
                <div className="timeline">
                  {gruposVisiveis.length === 0 && (
                    <div className="agenda-empty-month">Nenhum item encontrado para os filtros atuais.</div>
                  )}
                  {gruposVisiveis.map(({ data, itens }) => {
                    const dias = calcularDiasRestantes(data);
                    const rotulo = rotuloDia(data, dias);
                    const [, mes, dia] = data.split('-');
                    return (
                      <div key={data} className="timeline-day">
                        <div className="timeline-marker">
                          <span className={`timeline-dot ${rotulo.classe}`} />
                          <span className="timeline-line" />
                        </div>
                        <div className="timeline-content">
                          <div className="timeline-day-header">
                            <span className="timeline-daynum">{dia}</span>
                            <div className="timeline-day-info">
                              <span className="timeline-monthday">{MESES_CURTO[parseInt(mes, 10) - 1]} • {getDiaSemana(data)}</span>
                              {rotulo.texto && (
                                <span className={`timeline-badge ${rotulo.classe}`}>
                                  {rotulo.texto}{itens.length > 1 ? ` · ${itens.length} tarefas` : ''}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="timeline-tasks">
                            {itens.map(renderLinhaTarefa)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {temMaisDias && (
                    <button className="ver-mais-btn" onClick={() => setVisibleDaysCount(v => v + 3)}>
                      Ver mais dias
                    </button>
                  )}
                </div>
              )}

              {viewMode === 'lista' && (
                <div className="lista-view">
                  {itensParaTimeline.length === 0 && (
                    <div className="agenda-empty-month">Nenhum item encontrado.</div>
                  )}
                  {itensParaTimeline.map(item => (
                    <div key={`${item.tipo}-${item.id}`} className="lista-row-wrapper">
                      <span className="lista-data">{item.data_ref.split('-').reverse().join('/')}</span>
                      {renderLinhaTarefa(item)}
                    </div>
                  ))}
                </div>
              )}

              {viewMode === 'calendario' && (
                <div className="calendario-view">
                  <div className="mini-cal-header big">
                    <button onClick={() => mudarMes(-1)}><FaChevronLeft /></button>
                    <span>{MESES_LONGO[calMonth - 1]} {calYear}</span>
                    <button onClick={() => mudarMes(1)}><FaChevronRight /></button>
                  </div>
                  <div className="mini-cal-weekdays big">
                    {DIAS_SEMANA.map(d => <span key={d}>{d}</span>)}
                  </div>
                  <div className="mini-cal-grid big">
                    {gerarGradeCalendario().map((d, idx) => {
                      if (!d) return <span key={idx} className="cal-cell empty" />;
                      const key = formatarDataKey(d);
                      const qtd = itensComDataFiltrados.filter(i => i.data_ref === key).length;
                      return (
                        <button
                          key={idx}
                          className={`cal-cell big ${isHoje(d) ? 'today' : ''} ${selectedDay === key ? 'selected' : ''}`}
                          onClick={() => clicarDiaCalendario(d)}
                        >
                          <span>{d}</span>
                          {qtd > 0 && <span className="cal-count">{qtd}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {itensSemDataFiltrados.length > 0 && (
                <div className="sem-data-section">
                  <button className="sem-data-toggle" onClick={() => setHideSemData(!hideSemData)}>
                    Sem data ({itensSemDataFiltrados.length})
                    <span>{hideSemData ? 'Mostrar' : 'Ocultar'}</span>
                  </button>
                  {!hideSemData && (
                    <div className="sem-data-list">
                      {itensSemDataFiltrados.map(item => {
                        const isEditing = editingItemId === item.id && editingItemType === item.tipo;
                        return (
                          <div key={`${item.tipo}-${item.id}`} className="sem-data-item">
                            {isEditing ? (
                              <div className="date-edit-row" onClick={e => e.stopPropagation()}>
                                <input type="date" value={editingDate} onChange={(e) => setEditingDate(e.target.value)} />
                                <button onClick={handleSave}>Salvar</button>
                                <button onClick={handleCancel}>Cancelar</button>
                              </div>
                            ) : (
                              <button className="add-date-btn" onClick={(e) => handleDateClick(item, e)}>
                                <FaCalendarAlt /> Definir data
                              </button>
                            )}
                            {renderLinhaTarefa(item)}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </main>
          </div>
        )}

        <div className="agenda-footer">
          <span className="footer-updated">
            {lastUpdated ? `Última atualização: ${lastUpdated.toLocaleDateString('pt-BR')} ${lastUpdated.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}
          </span>
          <div className="footer-legend">
            <span><FaRegCircle className="status-dot tarefa" /> Tarefa</span>
            <span><FaRegCircle className="status-dot compromisso" /> Compromisso</span>
            <span><FaRegCircle className="status-dot atrasada" /> Atrasada</span>
            <span><FaCheckCircle className="status-dot concluida" /> Concluída</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Agenda;